import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'antd';
import { selectFieldValueI18n } from '@icp/form-renderer-core';
import { composeEvent, composeEventAsync } from '@icp/utils';
import { useSelector } from '../../../store';
import ValueLanguageSelect from '../../../ValueLanguageSelect';

const I18nInputMulti = forwardRef(function I18nInputMulti(props, ref) {
  const {
    id,
    value: valueProp,
    changeDelayToBlur,
    onChange,
    onBlur,
    onKeyDown,
    onKeyUp,
    ...other
    //
  } = props;

  const { i18n } = useTranslation();

  const [valueLanguage, setValueLanguage] = useState(i18n.language);
  const valueI18n = useSelector(selectFieldValueI18n(id, valueLanguage));
  const [value, setValue] = useState(valueI18n);
  // 让 keydown 和 keyup 之间 onChange 只会触发一次，防止一致按住 Enter 键反复触发 onChange
  const once = useRef(false);

  useEffect(() => {
    setValue(valueI18n);
  }, [valueI18n]);

  const handleChange = (event) => {
    setValue(event.target.value);
    if (!changeDelayToBlur) {
      onChange(event.target.value, valueLanguage);
    }
  };

  const handleBlur = (event) => {
    if (changeDelayToBlur) {
      onChange(event.target.value, valueLanguage);
    }
  };

  // 当 changeDelayToBlur 时，按回车键也需要触发 onChange。和原生 input 逻辑一致
  const handleKeyDown = (event) => {
    if (!changeDelayToBlur) {
      return;
    }

    if (once.current) {
      return;
    }

    if (event.key === 'Enter') {
      once.current = true;
      onChange(value, valueLanguage);
    }
  };

  const handleKeyUp = (event) => {
    if (!changeDelayToBlur) {
      return;
    }

    if (event.key === 'Enter') {
      once.current = false;
    }
  };

  return (
    <Input
      autoComplete="off"
      {...other}
      value={value ?? ''}
      onChange={handleChange}
      onBlur={composeEventAsync(handleBlur, onBlur)}
      onKeyDown={composeEvent(handleKeyDown, onKeyDown)}
      onKeyUp={composeEvent(handleKeyUp, onKeyUp)}
      addonBefore={<ValueLanguageSelect value={valueLanguage} onChange={setValueLanguage} />}
      ref={ref}
    />
  );
});

I18nInputMulti.propTypes = {
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  changeDelayToBlur: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
};

export default I18nInputMulti;
